import {configureStore} from '@reduxjs/toolkit'
import makerDataSlice, {PREVIEW_DEFAULT_VOLUME} from "./makerDataSlice";
import SlideData, {UploadStatus} from "../Interfaces/SlideData";
import MusicData, {MUSIC_TYPE_LOCAL} from "../Interfaces/MusicData";
import {v4 as uuidv4} from "uuid";
import * as Sentry from "@sentry/react";
import {captureException} from "@sentry/react";
import {OnBoardingStep} from "../Interfaces/OnBoardingStep";
import axios from "axios";
import {vkLoadStateUrl, vkSaveStateUrl} from "../Api/url";

export const LOCALSTORAGE_KEY: string = 'sl-local-storage';

// Делаем так, чтобы запрос на загрузку state'а был синхронным
const vkLoadState = (urlParams: URLSearchParams) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', vkLoadStateUrl + '?' + urlParams.toString(), false);
    xhr.send(null);
    if (xhr.status === 200) {
        return xhr.responseText;
    } else {
        captureException(new Error('Load state failed: ' + xhr.statusText));
    }
}

const saveStateTimeout = {
    id: null,
};

//MIDDLEWARE
// @ts-ignore
const localStorageMiddleware = ({getState}) => {
    const urlParams = new URLSearchParams(window.location.search);
    // Если VK - то не сохраняем state в localStorage, а сохраняем в БД на сервере
    if (urlParams.has('vk_app_id')) {
        // @ts-ignore
        return next => action => {
            const result = next(action);
            const getParams = {};
            const entries = urlParams.entries();
            Array.from(entries).map((entry) => {
                // @ts-ignore
                getParams[entry[0]] = entry[1];
            })

            if (saveStateTimeout.id !== null) clearTimeout(saveStateTimeout.id);
            // @ts-ignore
            saveStateTimeout.id = setTimeout(() => {
                axios.post(vkSaveStateUrl, JSON.stringify(getState()), {params: getParams})
                    .then(function (response) {
                    })
                    .catch(err => {
                        captureException(err);
                    });
            }, 3000);

            return result;
        }
    }

    // Если НЕ VK - сохраняем state в localStorage
    // @ts-ignore
    return next => action => {
        const result = next(action);
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(getState()));
        return result;
    };
};
const preloadState = () => {
    let data = undefined;
    const urlParams = new URLSearchParams(window.location.search);
    // Если VK - загружаем state с сервера
    if (urlParams.has('vk_app_id')) {
        const responseData = vkLoadState(urlParams);
        if (typeof responseData !== 'undefined') {
            data = JSON.parse(responseData)?.state;
        }
    } else {
        if (localStorage.getItem(LOCALSTORAGE_KEY) !== null && localStorage.getItem(LOCALSTORAGE_KEY) !== '') {
            data = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) ?? '');
        }
    }

    if (typeof data !== 'undefined') {
        // Sentry.setExtra('makerData-localStorage', data?.makerData);

        data = hydrateData(data);

        // Sentry.setExtra('makerData-reHydrated', data.makerData);

        return data;
    }
};

const hydrateData = (data: any) => {
    data.makerData.isLoaded = false;
    data.makerData.isPreviewRestored = false;
    data.makerData.isAddSlidePopupOpen = false;

    const twoDaysAgo = Math.round(new Date().getTime() / 1000) - 60 * 60 * 24 * 2;
    const thirtyDaysAgo = Math.round(new Date().getTime() / 1000) - 60 * 60 * 24 * 30;
    if (data.makerData.slides) {
        data.makerData.slides = data.makerData.slides.filter((slide: SlideData) => {
            if (typeof slide === 'undefined') {
                return false;
            }
            if (slide.status !== UploadStatus.ready && slide.status !== UploadStatus.uploadSuccess) {
                return false;
            }
            // если есть поле createdAt и файл был загружен больше чем 30 дней назад - скипаем
            if (
                typeof slide.createdAt !== 'undefined' && slide.createdAt !== null &&
                slide.createdAt! < thirtyDaysAgo
            ) {
                return false;
            }
            return true;
        });
    }
    if (data.makerData.music) {
        data.makerData.music = data.makerData.music.filter((music: MusicData) => {
            if (typeof music === 'undefined') {
                return false;
            }
            if (music.status !== UploadStatus.ready && music.status !== UploadStatus.uploadSuccess) {
                return false;
            }
            // восстанавливаем из localstorage только пользовательские мелодии
            if (music.type === MUSIC_TYPE_LOCAL) {
                return false;
            }
            // если есть поле createdAt и файл был загружен больше чем 2 дня назад - скипаем
            if (
                typeof music.createdAt !== 'undefined' && music.createdAt !== null &&
                music.createdAt! < thirtyDaysAgo
            ) {
                return false;
            }
            return true;
        });
    }
    if (typeof data.makerData.previewVolume === 'undefined') {
        data.makerData.previewVolume = PREVIEW_DEFAULT_VOLUME;
    }
    if (typeof data.makerData.userId === 'undefined' || data.makerData.userId == null) {
        data.makerData.userId = uuidv4().substring(0, 8);
    }
    Sentry.setUser({id: data.makerData.userId})
    data.makerData.isMusicPlaying = false;
    data.makerData.isPreviewPlaying = false;
    data.makerData.isAllPhotosUploaded = true;
    data.makerData.isAllMusicUploaded = true;

    // Если preview было сделано больше 2х дней назад - обнуляем всё, что связано с preview
    if (typeof data.makerData.previewCreatedAt !== 'undefined' && data.makerData.previewCreatedAt !== null &&
        data.makerData.previewCreatedAt < twoDaysAgo) {
        data.makerData.previewId = null;
        data.makerData.previewIdStateHash = null;
        data.makerData.previewData = null;
        data.makerData.previewCreatedAt = null;
        data.makerData.scenes = '';
        data.makerData.fullVideoPrice = null;
        data.makerData.fullVideoMakeTime = null;
        data.makerData.fullVideoMakeTimeStr = null;
    }

    data.makerData.promoCodeResponse = null;
    data.makerData.promoCode = '';
    data.makerData.promoCodeDiscount = 0;

    if (typeof data.makerData.onBoardingStep === 'undefined') {
        data.makerData.onBoardingStep = OnBoardingStep.themes;
    }
    if (typeof data.makerData.onBoardingShow === 'undefined') {
        data.makerData.onBoardingShow = true;
    }
    if (typeof data.makerData.setOnBoardingCurrentStepsArray === 'undefined') {
        data.makerData.setOnBoardingCurrentStepsArray = [];
    }

    if (typeof data.makerData.onBoardingStepsShown === 'undefined') {
        data.makerData.onBoardingStepsShown = [];
    }

    if (typeof data.makerData.musicUploadPidOnlyOneFile === 'undefined') {
        data.makerData.musicUploadPidOnlyOneFile = uuidv4();
    }

    data.makerData.cardSlugTmp = null;
    data.makerData.currentSlide = null;

    if (typeof data.makerData.cardsBeforePay === 'undefined') {
        data.makerData.cardsBeforePay = [];
    }

    return data;
}

export const store = configureStore({
    reducer: {
        makerData: makerDataSlice,
    },
    preloadedState: preloadState(),
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware().concat(localStorageMiddleware),
})

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch